"use client";

import _slicedToArray from "/codebuild/output/src538327727/src/actions-runner/_work/www-lwb-org-au/www-lwb-org-au/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import { DialogScopedContextProvider } from "./RS7LB2H4.js";
import { useDisclosureProviderContext } from "./S6EF7IVO.js";
import { createElement, createHook, forwardRef } from "./LMDWO4NN.js";
import { useStoreState } from "./YV4JVR4I.js";
import { useId, useMergeRefs, useSafeLayoutEffect, useWrapElement } from "./ABQUS43J.js";
import { __objRest, __spreadProps, __spreadValues } from "./3YLGPPWQ.js";

// src/disclosure/disclosure-content.tsx
import { invariant, removeUndefinedValues } from "@ariakit/core/utils/misc";
import { useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { jsx } from "react/jsx-runtime";
var TagName = "div";
function afterTimeout(timeoutMs, cb) {
  var timeoutId = setTimeout(cb, timeoutMs);
  return function () {
    return clearTimeout(timeoutId);
  };
}
function afterPaint(cb) {
  var raf = requestAnimationFrame(function () {
    raf = requestAnimationFrame(cb);
  });
  return function () {
    return cancelAnimationFrame(raf);
  };
}
function parseCSSTime() {
  for (var _len = arguments.length, times = new Array(_len), _key = 0; _key < _len; _key++) {
    times[_key] = arguments[_key];
  }
  return times.join(", ").split(", ").reduce(function (longestTime, currentTimeString) {
    var multiplier = currentTimeString.endsWith("ms") ? 1 : 1e3;
    var currentTime = Number.parseFloat(currentTimeString || "0s") * multiplier;
    if (currentTime > longestTime) return currentTime;
    return longestTime;
  }, 0);
}
function isHidden(mounted, hidden, alwaysVisible) {
  return !alwaysVisible && hidden !== false && (!mounted || !!hidden);
}
var useDisclosureContent = createHook(function useDisclosureContent2(_a) {
  var _b = _a,
    store = _b.store,
    alwaysVisible = _b.alwaysVisible,
    props = __objRest(_b, ["store", "alwaysVisible"]);
  var context = useDisclosureProviderContext();
  store = store || context;
  invariant(store, process.env.NODE_ENV !== "production" && "DisclosureContent must receive a `store` prop or be wrapped in a DisclosureProvider component.");
  var ref = useRef(null);
  var id = useId(props.id);
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    transition = _useState2[0],
    setTransition = _useState2[1];
  var open = store.useState("open");
  var mounted = store.useState("mounted");
  var animated = store.useState("animated");
  var contentElement = store.useState("contentElement");
  var otherElement = useStoreState(store.disclosure, "contentElement");
  useSafeLayoutEffect(function () {
    if (!ref.current) return;
    store == null ? void 0 : store.setContentElement(ref.current);
  }, [store]);
  useSafeLayoutEffect(function () {
    var previousAnimated;
    store == null ? void 0 : store.setState("animated", function (animated2) {
      previousAnimated = animated2;
      return true;
    });
    return function () {
      if (previousAnimated === void 0) return;
      store == null ? void 0 : store.setState("animated", previousAnimated);
    };
  }, [store]);
  useSafeLayoutEffect(function () {
    if (!animated) return;
    if (!(contentElement == null ? void 0 : contentElement.isConnected)) {
      setTransition(null);
      return;
    }
    return afterPaint(function () {
      setTransition(open ? "enter" : mounted ? "leave" : null);
    });
  }, [animated, contentElement, open, mounted]);
  useSafeLayoutEffect(function () {
    if (!store) return;
    if (!animated) return;
    if (!transition) return;
    if (!contentElement) return;
    var stopAnimation = function stopAnimation() {
      return store == null ? void 0 : store.setState("animating", false);
    };
    var stopAnimationSync = function stopAnimationSync() {
      return flushSync(stopAnimation);
    };
    if (transition === "leave" && open) return;
    if (transition === "enter" && !open) return;
    if (typeof animated === "number") {
      var timeout2 = animated;
      return afterTimeout(timeout2, stopAnimationSync);
    }
    var _getComputedStyle = getComputedStyle(contentElement),
      transitionDuration = _getComputedStyle.transitionDuration,
      animationDuration = _getComputedStyle.animationDuration,
      transitionDelay = _getComputedStyle.transitionDelay,
      animationDelay = _getComputedStyle.animationDelay;
    var _ref = otherElement ? getComputedStyle(otherElement) : {},
      _ref$transitionDurati = _ref.transitionDuration,
      transitionDuration2 = _ref$transitionDurati === void 0 ? "0" : _ref$transitionDurati,
      _ref$animationDuratio = _ref.animationDuration,
      animationDuration2 = _ref$animationDuratio === void 0 ? "0" : _ref$animationDuratio,
      _ref$transitionDelay = _ref.transitionDelay,
      transitionDelay2 = _ref$transitionDelay === void 0 ? "0" : _ref$transitionDelay,
      _ref$animationDelay = _ref.animationDelay,
      animationDelay2 = _ref$animationDelay === void 0 ? "0" : _ref$animationDelay;
    var delay = parseCSSTime(transitionDelay, animationDelay, transitionDelay2, animationDelay2);
    var duration = parseCSSTime(transitionDuration, animationDuration, transitionDuration2, animationDuration2);
    var timeout = delay + duration;
    if (!timeout) {
      if (transition === "enter") {
        store.setState("animated", false);
      }
      stopAnimation();
      return;
    }
    var frameRate = 1e3 / 60;
    var maxTimeout = Math.max(timeout - frameRate, 0);
    return afterTimeout(maxTimeout, stopAnimationSync);
  }, [store, animated, contentElement, otherElement, open, transition]);
  props = useWrapElement(props, function (element) {
    return /* @__PURE__ */jsx(DialogScopedContextProvider, {
      value: store,
      children: element
    });
  }, [store]);
  var hidden = isHidden(mounted, props.hidden, alwaysVisible);
  var styleProp = props.style;
  var style = useMemo(function () {
    if (hidden) {
      return __spreadProps(__spreadValues({}, styleProp), {
        display: "none"
      });
    }
    return styleProp;
  }, [hidden, styleProp]);
  props = __spreadProps(__spreadValues({
    id: id,
    "data-open": open || void 0,
    "data-enter": transition === "enter" || void 0,
    "data-leave": transition === "leave" || void 0,
    hidden: hidden
  }, props), {
    ref: useMergeRefs(id ? store.setContentElement : null, ref, props.ref),
    style: style
  });
  return removeUndefinedValues(props);
});
var DisclosureContentImpl = forwardRef(function DisclosureContentImpl2(props) {
  var htmlProps = useDisclosureContent(props);
  return createElement(TagName, htmlProps);
});
var DisclosureContent = forwardRef(function DisclosureContent2(_a) {
  var _b = _a,
    unmountOnHide = _b.unmountOnHide,
    props = __objRest(_b, ["unmountOnHide"]);
  var context = useDisclosureProviderContext();
  var store = props.store || context;
  var mounted = useStoreState(store, function (state) {
    return !unmountOnHide || (state == null ? void 0 : state.mounted);
  });
  if (mounted === false) return null;
  return /* @__PURE__ */jsx(DisclosureContentImpl, __spreadValues({}, props));
});
export { isHidden, useDisclosureContent, DisclosureContent };